<script lang="ts" setup>
import { QBtn } from 'quasar'
import { computed } from 'vue'
import { Color } from '@previo/codebooks'
import { PMaxLines } from '../typography/types'
import { PTheme } from '../../types/PTheme'
import PBadge from '../badge/PBadge.vue'
import { PBadgeColor } from '../badge/types'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextLink from '../typography/links/PTextLink.vue'

interface Props {
  text?: string
  theme?: PTheme.LIGHT | PTheme.DARK
  iconStart?: string
  iconEnd?: string
  badgeText?: string | number | null
  outline?: boolean
  grow?: boolean
  disable?: boolean
  cy: string
}

const props = withDefaults(defineProps<Props>(), {
  theme: PTheme.LIGHT,
  outline: false,
  grow: false,
  disable: false,
})

const emits = defineEmits(['click'])

const textColor = computed<Color.GREY_600 | Color.WHITE>(() => {
  switch (props.theme) {
    case PTheme.LIGHT:
      return Color.GREY_600
    case PTheme.DARK:
      return Color.WHITE

    default:
      throw 'Invalid theme props in PBtnMenu component'
  }
})

const iconColor = computed<Color.PRIMARY | Color.GREY_500 | Color.WHITE>(() => {
  switch (props.theme) {
    case PTheme.LIGHT:
      return props.disable ? Color.GREY_500 : Color.PRIMARY
    case PTheme.DARK:
      return Color.WHITE

    default:
      throw 'Invalid theme props in PBtnMenu component'
  }
})

const click = (): void => {
  emits('click')
}
</script>

<template>
  <q-btn
    class="ds-btn-menu"
    :class="{ 'ds-btn-menu-outline-dark': outline && theme === PTheme.DARK, 'p-full-width': grow }"
    unelevated
    no-caps
    :outline="outline"
    :text-color="textColor"
    :disable="disable"
    :data-cy="cy"
    no-wrap
    @click="click()"
  >
    <div class="p-flex p-no-wrap">
      <p-icon v-if="iconStart" :icon="iconStart" :color="iconColor" />
      <template v-if="text">
        <p-space v-if="iconStart" :mobile="PSpaceSize.SM" />
        <p-text-link :text="text" :max-lines="PMaxLines.ONE" />
        <p-space :mobile="PSpaceSize.XS" />
      </template>
      <p-icon v-if="iconEnd" :icon="iconEnd" :color="iconColor" />
    </div>

    <slot />

    <p-badge v-if="badgeText" :text="badgeText" :color="PBadgeColor.INFO" floating />
  </q-btn>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/mixins';
@import '../../assets/scss/shared/brand.module';

.ds-btn-menu {
  @include mobile {
    padding: 10px;
  }

  @include laptopAndLarge {
    padding: 4px 6px 4px 6px;
  }

  &.q-btn--outline::before {
    border-color: getColor('grey200');
  }

  &.ds-btn-menu-outline-dark.q-btn--outline::before {
    border-color: getColor('whiteTransparent');
  }
}
</style>
